/**
 * Similar to postgres's coalesce function, this function will return the first
 * value if it is not null or undefined; otherwise, it will return the second
 * value. This differs from `getWithDefault` because it allows nulls and undefined
 * to be part of the return value, whereas `getWithDefault` requires a truthy
 * default value.
 *
 * This function is strongly typed.
 *
 * Credit: https://stackoverflow.com/a/54370023
 */
export function coalesce<T extends any[]>(
  ...args: T
): ExcludeNullIfAnyNotNull<T>;
export function coalesce<T extends any[]>(...args: T): T[number] {
  for (let i = 0; i < args.length; i++) {
    // args[i] === args[i] is to avoid NaN, because NaN !== NaN
    if (args[i] != null && args[i] === args[i]) {
      return args[i];
    }
  }
  return args[args.length - 1];
}

type ExcludeNullIfAnyNotNullHelper<T> = {
  [P in keyof T]-?:
    | (null extends T[P] ? never : null)
    | (undefined extends T[P] ? never : undefined);
}[keyof T];

type ExcludeNullIfAnyNotNull<T extends any[]> = Exclude<
  T[number],
  ExcludeNullIfAnyNotNullHelper<T>
>;

type Falsies = undefined | null | false | 0 | "";

/**
 * Similar to the above `coalesce`, this function will return the first truthy
 * value it encounters; otherwise, it will return the last value in the list.
 *
 * Note: "Truthy" here follows JavaScript semantics, meaning any non-falsy value.
 */
export function coalesceFirstTruthy<T extends any[], Last>(
  ...args: [...T, Last]
): Exclude<T[number], Falsies> | Last {
  for (let i = 0; i < args.length; i++) {
    if (args[i]) {
      return args[i];
    }
  }
  return args[args.length - 1];
}
